/**
 * Calibrating the address rectangle from one scan, without anyone reading the card.
 *
 * The service measures where ink is. It does not know what any band says, and finding out would
 * mean recognising text on an identity document for no reason other than to place a rectangle. So
 * the question "which part of this card is the domicile block?" is put to the operator, who is
 * holding the card and can see their own screen.
 *
 * Everything here is geometry. No value read from a document passes through this module, and
 * nothing in it is persisted.
 *
 * **Two things changed on 2026-09-15.** A calibration answer is now a collection with one entry per
 * illumination, because which frame this card segments on is an open question and one measurement
 * cannot answer a comparison. And an operator who is offered no safe automatic band can now draw a
 * rectangle instead - on 2026-09-14 they were offered exactly one block covering the whole card,
 * refused it correctly, and had no other route.
 */

/** Which illumination a measurement was taken from. */
export type CalibrationSource = 'visible' | 'infrared';

/** Which space a set of fractions is expressed in. */
export type CoordinateBasis = 'captureFrame' | 'detectedDocument';

/** Whether an independent document extent was established. */
export type DocumentBoundsStatus = 'detected' | 'unavailable';

/** How a candidate rectangle came about. */
export type CalibrationMode = 'automatic-bands' | 'manual-rectangle';

/** One band of ink the service found, as fractions of the coordinate basis. */
export interface CaptureBand {
    readonly index: number;
    readonly top: number;
    readonly bottom: number;
    readonly left: number;
    readonly right: number;
    readonly inkedColumnRatio: number;
    /**
     * Whether this band belongs to a structurally identified machine-readable zone.
     *
     * Set only from a pair of narrow lines low on the document. It used to be one band's own ink
     * density, which is how a block covering the whole card came to be labelled as the zone.
     */
    readonly looksLikeCodeline: boolean;
    readonly meanResidual?: number;
}

/** Bounded aggregates describing how one source's measurement went. Diagnostic only. */
export interface CaptureDiagnostics {
    readonly source: string;
    readonly byteLength: number;
    readonly decodeAttempted: boolean;
    readonly decodeSucceeded: boolean;
    readonly separable: boolean;
    readonly segmentationMethod: string;
    readonly residualThreshold: number;
    readonly separability: number;
    readonly inkFraction: number;
    readonly rowInkThreshold: number;
    readonly inkedRowRatio: number;
    readonly rawBandCount: number;
    readonly acceptedBandCount: number;
    readonly rejectedBandCount: number;
    readonly rejectedReasons: readonly { readonly reason: string; readonly count: number }[];
    readonly mrzCandidateCount: number;
}

/** Where ink is on one capture, from one illumination. Geometry only. */
export interface CaptureCalibration {
    readonly source: CalibrationSource;
    readonly coordinateBasis: CoordinateBasis;
    readonly width: number;
    readonly height: number;
    readonly aspectRatio: number;
    readonly appliedRotationDegrees: number;
    readonly documentLeft: number;
    readonly documentTop: number;
    readonly documentRight: number;
    readonly documentBottom: number;
    readonly documentBoundsStatus: DocumentBoundsStatus;
    readonly documentBoundsConfidence: number;
    readonly bands: readonly CaptureBand[];
    readonly codelineBandIndex: number | null;
    readonly mrzAvailable: boolean;
    readonly mrzTop: number;
    readonly mrzBottom: number;
    readonly mrzLeft: number;
    readonly mrzRight: number;
    readonly usable: boolean;
    readonly diagnostics?: CaptureDiagnostics | null;
}

/** A rectangle in the coordinate basis of its calibration. */
export interface NormalizedRect {
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
}

/** Why a rectangle cannot be recorded as a calibration. */
export type CandidateProblem =
    /** Nothing selected and nothing drawn. */
    | 'empty'
    /** Smaller than any printed block. */
    | 'tooSmall'
    /** Covers substantially the whole frame. */
    | 'fullFrame'
    /** Larger than a printed address block can be. */
    | 'oversized'
    /** Overlaps a machine-readable zone the service actually identified. */
    | 'overlapsMrz'
    /** Catches automatic bands the operator did not choose. */
    | 'overlapsUnselected'
    /** Outside the image. */
    | 'outOfBounds';

/** What an operator's selection produces, and what it can be checked against. */
export interface CalibrationCandidate {
    readonly source: CalibrationSource;
    readonly coordinateBasis: CoordinateBasis;
    readonly mode: CalibrationMode;
    readonly rect: NormalizedRect;
    readonly selected: readonly number[];
    /** True when the rectangle does not touch an identified machine-readable zone. */
    readonly excludesMrz: boolean;
    /**
     * Whether the zone's location is known at all.
     *
     * False means the exclusion above could not be checked, not that there is nothing to exclude.
     * A candidate in that state needs the operator to confirm it themselves.
     */
    readonly mrzKnown: boolean;
    /** Indices of automatic bands the rectangle overlaps but the operator did not select. */
    readonly overlapsUnselected: readonly number[];
    /** Every reason this rectangle is not safe. Empty when it is. */
    readonly problems: readonly CandidateProblem[];
    /** Whether the rectangle is structurally safe to record. */
    readonly safe: boolean;
}

/**
 * Limits, mirrored from the service's own band validation.
 *
 * Deliberately the same numbers as `CaptureCalibrator`. A rectangle the service would refuse as a
 * band must not become acceptable because a person drew it by hand, and the operator should be told
 * while they are drawing rather than after they have confirmed.
 */
export const CANDIDATE_LIMITS = {
    /** At or above this height and width together, a rectangle is the frame and not a block. */
    fullFrameHeight: 0.85,
    fullFrameWidth: 0.9,
    /** Taller than this, it is not a block of printed lines. */
    maximumHeight: 0.45,
    /** Larger than this share of the basis by area, it is not a printed block. */
    maximumArea: 0.35,
    /** Below these, there is nothing inside the rectangle to read. */
    minimumHeight: 0.015,
    minimumWidth: 0.05,
} as const;

/**
 * A small margin around the selected bands.
 *
 * Band edges are the first and last row carrying ink, so a rectangle drawn exactly on them clips
 * ascenders and descenders. One percent of the capture is enough to avoid that and small enough
 * that it cannot swallow a neighbouring line - the overlap check below would catch it if it did.
 */
const PADDING = 0.01;

function clamp(value: number): number {
    return Math.min(1, Math.max(0, value));
}

function overlapsBand(rect: NormalizedRect, band: CaptureBand): boolean {
    const rectBottom = rect.y + rect.h;
    const rectRight = rect.x + rect.w;

    return band.top < rectBottom && rect.y < band.bottom && band.left < rectRight && rect.x < band.right;
}

/**
 * Whether a value from the wire is a calibration that can actually be worked with.
 *
 * **The guard the page needed and did not have.** The body is read with an unchecked cast, so a
 * null - or an object whose `bands` did not survive - arrives wearing the right type. Asking about
 * the shape rather than trusting the declaration is the only thing that can tell the difference.
 *
 * Structural, and now also source-aware: a measurement that does not name its illumination cannot
 * be drawn over a preview, because there is no way to know which preview it belongs to. A service
 * that predates the source-aware contract therefore fails closed here rather than having its single
 * unlabelled measurement assumed to be the visible frame.
 *
 * @param value Anything the service put in a calibration property.
 */
export function isUsableCalibration(value: unknown): value is CaptureCalibration {
    if (typeof value !== 'object' || value === null) {
        return false;
    }

    const candidate = value as CaptureCalibration;

    return (
        Array.isArray(candidate.bands) &&
        (candidate.source === 'visible' || candidate.source === 'infrared')
    );
}

/**
 * The measurements a scan produced, in a fixed order, with anything unreadable dropped.
 *
 * Order is presentation only and carries no preference. Nothing here ranks the sources, and nothing
 * selects one: the operator does that, which is the whole point of measuring both.
 *
 * @param values Whatever the service put in the calibrations property.
 */
export function readCalibrations(values: unknown): readonly CaptureCalibration[] {
    if (!Array.isArray(values)) {
        return [];
    }

    const usable = values.filter(isUsableCalibration);
    const order: CalibrationSource[] = ['visible', 'infrared'];

    return order
        .map(source => usable.find(entry => entry.source === source))
        .filter((entry): entry is CaptureCalibration => entry !== undefined);
}

/** An answer that selects nothing and claims nothing. */
function nothingSelected(
    calibration: CaptureCalibration,
    mode: CalibrationMode,
): CalibrationCandidate {
    return {
        source: calibration.source,
        coordinateBasis: calibration.coordinateBasis,
        mode,
        rect: { x: 0, y: 0, w: 0, h: 0 },
        selected: [],
        // A rectangle that does not exist cannot be overlapping anything.
        excludesMrz: true,
        mrzKnown: calibration.mrzAvailable,
        overlapsUnselected: [],
        problems: ['empty'],
        safe: false,
    };
}

/**
 * Checks one rectangle against every structural rule, whoever produced it.
 *
 * Shared by the automatic and manual paths on purpose. A hand-drawn rectangle is not held to a
 * looser standard than a selection of measured bands, and an operator who draws something the
 * service would have refused is told so before they confirm it.
 */
function evaluate(
    calibration: CaptureCalibration,
    rect: NormalizedRect,
    selected: readonly number[],
    mode: CalibrationMode,
): CalibrationCandidate {
    const problems: CandidateProblem[] = [];

    if (rect.w <= 0 || rect.h <= 0) {
        return nothingSelected(calibration, mode);
    }

    if (rect.x < 0 || rect.y < 0 || rect.x + rect.w > 1.000001 || rect.y + rect.h > 1.000001) {
        problems.push('outOfBounds');
    }

    if (rect.h >= CANDIDATE_LIMITS.fullFrameHeight && rect.w >= CANDIDATE_LIMITS.fullFrameWidth) {
        problems.push('fullFrame');
    }

    if (rect.h < CANDIDATE_LIMITS.minimumHeight || rect.w < CANDIDATE_LIMITS.minimumWidth) {
        problems.push('tooSmall');
    }

    if (rect.h > CANDIDATE_LIMITS.maximumHeight || rect.h * rect.w > CANDIDATE_LIMITS.maximumArea) {
        problems.push('oversized');
    }

    // Only when the service actually identified a zone. When it did not, the exclusion cannot be
    // checked at all - which is a different thing from there being nothing to exclude, and the
    // operator is asked to confirm it rather than being told it is fine.
    const overlapsMrz =
        calibration.mrzAvailable &&
        calibration.mrzTop < rect.y + rect.h &&
        rect.y < calibration.mrzBottom &&
        calibration.mrzLeft < rect.x + rect.w &&
        rect.x < calibration.mrzRight;

    if (overlapsMrz) {
        problems.push('overlapsMrz');
    }

    // Anything the rectangle catches that the operator did not choose is a problem, whether it is
    // the codeline, the name block or the personal number. The service cannot tell which of those a
    // band is, so the check is deliberately about *unselected* bands rather than about named ones.
    const overlapsUnselected = calibration.bands
        .filter(band => !selected.includes(band.index) && overlapsBand(rect, band))
        .map(band => band.index);

    if (mode === 'automatic-bands' && overlapsUnselected.length > 0) {
        problems.push('overlapsUnselected');
    }

    return {
        source: calibration.source,
        coordinateBasis: calibration.coordinateBasis,
        mode,
        rect,
        selected: [...selected].sort((left, right) => left - right),
        excludesMrz: !overlapsMrz,
        mrzKnown: calibration.mrzAvailable,
        overlapsUnselected,
        problems,
        safe: problems.length === 0,
    };
}

/**
 * Builds the candidate rectangle from the bands the operator picked.
 *
 * **Fails closed on anything it cannot measure.** The caller is supposed to have established that
 * there is a calibration to work with, and on 2026-09-14 the caller's check was `!== undefined`
 * against a wire value of `null` - so this function read `.bands` off null and took the entire
 * React application down with it.
 *
 * @param calibration What the service measured, or whatever arrived instead.
 * @param selected The band indices the operator says cover the domicile block.
 */
export function buildCalibrationCandidate(
    calibration: CaptureCalibration | null | undefined,
    selected: readonly number[],
): CalibrationCandidate | null {
    if (!isUsableCalibration(calibration)) {
        return null;
    }

    const chosen = calibration.bands.filter(band => selected.includes(band.index));

    if (chosen.length === 0) {
        return nothingSelected(calibration, 'automatic-bands');
    }

    const top = clamp(Math.min(...chosen.map(band => band.top)) - PADDING);
    const bottom = clamp(Math.max(...chosen.map(band => band.bottom)) + PADDING);
    const left = clamp(Math.min(...chosen.map(band => band.left)) - PADDING);
    const right = clamp(Math.max(...chosen.map(band => band.right)) + PADDING);

    return evaluate(
        calibration,
        { x: left, y: top, w: right - left, h: bottom - top },
        selected,
        'automatic-bands',
    );
}

/**
 * Builds the candidate from a rectangle the operator drew.
 *
 * The fallback that did not exist on 2026-09-14. Automatic segmentation offered one block covering
 * the whole card, the operator refused it, and the physical scan ended with nothing recorded. Every
 * structural rule the automatic path is held to applies here unchanged - a hand-drawn rectangle is
 * not a licence to crop the whole document.
 *
 * @param calibration The measurement the rectangle was drawn over.
 * @param rect The rectangle, in the calibration's coordinate basis.
 */
export function buildManualCandidate(
    calibration: CaptureCalibration | null | undefined,
    rect: NormalizedRect | null,
): CalibrationCandidate | null {
    if (!isUsableCalibration(calibration)) {
        return null;
    }

    if (rect === null) {
        return nothingSelected(calibration, 'manual-rectangle');
    }

    // Clamped per component, and deliberately not shrunk to fit. Keeping a rectangle inside the
    // image is the drawing helpers' job - `rectFromCorners` and `adjustRect` both do it, so nothing
    // the panel produces can run off the edge. Quietly trimming one here instead would hand back a
    // rectangle the caller did not ask for and call it safe.
    const clamped: NormalizedRect = {
        x: clamp(rect.x),
        y: clamp(rect.y),
        w: clamp(rect.w),
        h: clamp(rect.h),
    };

    return evaluate(calibration, clamped, [], 'manual-rectangle');
}

/**
 * Turns two drag corners into a normalised rectangle, whichever way round they were drawn.
 *
 * @param from One corner, 0..1.
 * @param to The other corner, 0..1.
 */
export function rectFromCorners(
    from: { readonly x: number; readonly y: number },
    to: { readonly x: number; readonly y: number },
): NormalizedRect {
    const left = clamp(Math.min(from.x, to.x));
    const right = clamp(Math.max(from.x, to.x));
    const top = clamp(Math.min(from.y, to.y));
    const bottom = clamp(Math.max(from.y, to.y));

    return { x: left, y: top, w: right - left, h: bottom - top };
}

/**
 * Moves or resizes a rectangle by a step, staying inside the image.
 *
 * Exists so the rectangle is adjustable from the keyboard. A control that can only be operated by
 * dragging a mouse is not usable by every operator, and this one gates a physical calibration run.
 *
 * @param rect The rectangle to adjust.
 * @param edit What to change.
 */
export function adjustRect(
    rect: NormalizedRect,
    edit: {
        readonly dx?: number;
        readonly dy?: number;
        readonly dw?: number;
        readonly dh?: number;
    },
): NormalizedRect {
    const w = Math.max(0, Math.min(1, rect.w + (edit.dw ?? 0)));
    const h = Math.max(0, Math.min(1, rect.h + (edit.dh ?? 0)));
    const x = Math.max(0, Math.min(1 - w, rect.x + (edit.dx ?? 0)));
    const y = Math.max(0, Math.min(1 - h, rect.y + (edit.dy ?? 0)));

    return { x, y, w, h };
}

/**
 * The template fragment a confirmed candidate would contribute.
 *
 * Returned as text so an operator can carry it out of the session without anything being persisted
 * here. It is pure geometry and provenance - a rectangle, an illumination, a coordinate basis and
 * the capture's pixel size - and contains nothing read from the card.
 *
 * @param calibration What the service measured.
 * @param candidate The confirmed rectangle.
 */
export function describeCalibrationGeometry(
    calibration: CaptureCalibration,
    candidate: CalibrationCandidate,
): string {
    const round = (value: number) => Number(value.toFixed(4));

    return JSON.stringify(
        {
            templateId: 'ro-legacy-id-1997',
            calibrationVersion: 1,
            captureDefinition: {
                source: candidate.source,
                coordinateBasis: candidate.coordinateBasis,
                rectangle: [
                    round(candidate.rect.x),
                    round(candidate.rect.y),
                    round(candidate.rect.x + candidate.rect.w),
                    round(candidate.rect.y + candidate.rect.h),
                ],
                imageWidth: calibration.width,
                imageHeight: calibration.height,
                selectionMode: candidate.mode,
            },
            evidence: {
                aspectRatio: round(calibration.aspectRatio),
                appliedRotationDegrees: calibration.appliedRotationDegrees,
                documentBoundsStatus: calibration.documentBoundsStatus,
                documentBounds: [
                    round(calibration.documentLeft),
                    round(calibration.documentTop),
                    round(calibration.documentRight),
                    round(calibration.documentBottom),
                ],
                bandCount: calibration.bands.length,
                mrzAvailable: calibration.mrzAvailable,
                codelineBandIndex: calibration.codelineBandIndex,
                selectedBands: candidate.selected,
                excludesMrz: candidate.excludesMrz,
                overlapsUnselected: candidate.overlapsUnselected,
            },
        },
        null,
        2,
    );
}
